/**
 * Общие утилиты для форматирования и валидации госномеров РФ.
 *
 * Поддерживаются два формата:
 *   Тип 1 (стандартный):  А 123 АА 111   — буква, 3 цифры, 2 буквы, 2-3 цифры
 *   Тип 2 (прицепы и пр.): 1234 АА 111    — 4 цифры, 2 буквы, 2-3 цифры
 *
 * Формат определяется автоматически по первому символу чистого номера.
 */

export const VALID_LETTERS = 'АВЕКМНОРСТУХ'

/**
 * Паттерны валидации (без пробелов).
 *   Тип 1: /^[АВЕКМНОРСТУХ]\d{3}[АВЕКМНОРСТУХ]{2}\d{2,3}$/
 *   Тип 2: /^\d{4}[АВЕКМНОРСТУХ]{2}\d{2,3}$/
 */
export const PATTERN_TYPE1 = new RegExp(
  `^[${VALID_LETTERS}]\\d{3}[${VALID_LETTERS}]{2}\\d{2,3}$`
)
export const PATTERN_TYPE2 = new RegExp(
  `^\\d{4}[${VALID_LETTERS}]{2}\\d{2,3}$`
)

/**
 * Возвращает тип формата: 1 (стандартный) или 2 (прицеп).
 * Определяется по первому символу: буква → 1, цифра → 2.
 * Если строка пустая — null.
 */
function detectType(clean) {
  if (!clean) return null
  if (/^\d/.test(clean)) return 2
  return 1
}

/**
 * Форматирует чистый номер (без пробелов) для отображения.
 *   Тип 1: А123АА111 → А 123 АА 111
 *   Тип 2: 1234АА111 → 1234 АА 111
 */
export function formatForDisplay(clean) {
  if (!clean) return ''
  const type = detectType(clean)
  const chars = clean.split('')

  if (type === 2) {
    // 1234 АА 111 — пробелы после 4 и 6
    const parts = []
    for (let i = 0; i < chars.length; i++) {
      if (i === 4 || i === 6) parts.push(' ')
      parts.push(chars[i])
    }
    return parts.join('')
  }

  // Тип 1: А 123 АА 111 — пробелы после 1, 4, 6
  const parts = []
  for (let i = 0; i < chars.length; i++) {
    if (i === 1 || i === 4 || i === 6) parts.push(' ')
    parts.push(chars[i])
  }
  return parts.join('')
}

/**
 * Обрабатывает ввод пользователя: фильтрует недопустимые символы,
 * применяет позиционную маску и возвращает { formatted, clean }.
 *
 * @param {string} raw - сырой ввод из поля
 * @returns {{ formatted: string, clean: string }}
 */
export function processInput(raw) {
  let clean = raw.replace(/\s/g, '').toUpperCase()
  if (clean.length > 9) clean = clean.slice(0, 9)
  if (!clean) return { formatted: '', clean: '' }

  const type = detectType(clean)
  if (!type) return { formatted: '', clean: '' }

  // Строим чистый номер по позиционной маске
  let result = ''
  for (let i = 0; i < clean.length && result.length < 9; i++) {
    const ch = clean[i]
    const pos = result.length

    if (type === 1) {
      // А 123 АА 111
      if (pos === 0 || pos === 4 || pos === 5) {
        if (VALID_LETTERS.includes(ch)) result += ch
      } else if (pos === 1 || pos === 2 || pos === 3 || pos === 6 || pos === 7 || pos === 8) {
        if (/\d/.test(ch)) result += ch
      }
    } else {
      // 1234 АА 111
      if (pos === 0 || pos === 1 || pos === 2 || pos === 3 || pos === 6 || pos === 7 || pos === 8) {
        if (/\d/.test(ch)) result += ch
      } else if (pos === 4 || pos === 5) {
        if (VALID_LETTERS.includes(ch)) result += ch
      }
    }
  }

  return {
    formatted: formatForDisplay(result),
    clean: result
  }
}

/**
 * Валидирует чистый номер (без пробелов).
 * Пустая строка считается валидной (поле не обязательно).
 * Дополнительно: цифровая часть региона не может быть '000'.
 */
export function isValid(clean) {
  if (!clean) return true

  const type1Valid = PATTERN_TYPE1.test(clean)
  const type2Valid = PATTERN_TYPE2.test(clean)

  if (!type1Valid && !type2Valid) return false

  // Регион не может быть 000
  const digits = clean.match(/\d+/g)
  if (digits) {
    const region = digits[digits.length - 1]
    if (region === '000') return false
  }

  return true
}
